"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { Button } from "@/components/ui/button";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>,
      ) => string | number;
      remove: (widgetId: string | number) => void;
    };
  }
}

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Cloudflare Turnstile widget.
 *
 * Script URL uses the bare endpoint (no ?render=explicit) so Cloudflare
 * discovers the pre-existing container div rather than injecting the iframe
 * via innerHTML. This avoids the Trusted Types violation that ?render=explicit
 * triggers in Chromium: Cloudflare's JS calls innerHTML when it creates the
 * widget, and Chromium's native Trusted Types enforcement blocks it, so the
 * iframe is never created and the submit button stays disabled forever.
 *
 * The page COOP header must be same-origin-allow-popups (set in
 * next.config.ts) so Turnstile's cross-origin postMessage completes.
 *
 * Script is lazy-loaded: injected only once the widget scrolls into the
 * viewport (200px pre-load margin).
 */

type TurnstileWidgetProps = {
  action: string;
  className?: string;
  onTokenChange: (token: string) => void;
  onStatusChange?: (status: TurnstileWidgetStatus, message?: string) => void;
  resetKey?: number;
};

export type TurnstileWidgetStatus =
  | "loading"
  | "ready"
  | "solved"
  | "error";

export function TurnstileWidget({
  action,
  className,
  onTokenChange,
  onStatusChange,
  resetKey = 0,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | number | null>(null);
  // inViewport gates whether the Cloudflare script is injected at all.
  // Until the widget scrolls into view, no Turnstile JS is fetched and no
  // iframe is created, so the Trusted Types errors it generates are avoided.
  const [inViewport, setInViewport] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [scriptReady, setScriptReady] = useState(
    () => typeof window !== "undefined" && Boolean(window.turnstile),
  );
  const [scriptFailed, setScriptFailed] = useState(false);
  const [widgetStatus, setWidgetStatus] =
    useState<TurnstileWidgetStatus>("loading");
  const [statusMessage, setStatusMessage] =
    useState<string>("Security check loading...");
  const [retryKey, setRetryKey] = useState(0);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

  const updateStatus = useCallback((status: TurnstileWidgetStatus, message?: string) => {
    setWidgetStatus(status);
    setStatusMessage(message ?? "");
    onStatusChange?.(status, message);
  }, [onStatusChange]);

  // Lazy-load gate: only start loading Turnstile when the widget is visible.
  // This prevents the Cloudflare script (and its iframe) from running on pages
  // where the user never scrolls to the email-capture section.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    // If already in viewport on mount (e.g. /submit page above fold), fire immediately.
    if (typeof window !== "undefined" && Boolean(window.turnstile)) {
      setInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }, // preload 200px before visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_COSTDOC_E2E === "1" || siteKey === "e2e-bypass") {
      onTokenChange("e2e-bypass");
      onStatusChange?.("solved", "Verification challenge bypassed for E2E.");
      return () => {
        onTokenChange("");
      };
    }

    if (!siteKey && process.env.NODE_ENV !== "production") {
      onTokenChange("dev-bypass");
      onStatusChange?.("solved", "Verification challenge bypassed in local development.");
      return () => {
        onTokenChange("");
      };
    }

    if (!siteKey) {
      onStatusChange?.(
        "error",
        "Verification is temporarily unavailable. Please try again later.",
      );
      return;
    }

    if (scriptFailed) {
      onStatusChange?.(
        "error",
        "We couldn't load the verification challenge. Retry and try again.",
      );
      return;
    }

    if (!scriptReady || !containerRef.current) {
      onStatusChange?.("loading", "Security check loading...");
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const maxAttempts = 30;

    onTokenChange("");
    onStatusChange?.("loading", "Security check loading...");

    const renderWidget = () => {
      if (cancelled) return;

      if (!containerRef.current || !window.turnstile) {
        if (attempts >= maxAttempts) {
          updateStatus(
            "error",
            "Verification didn't finish loading. Retry and try again.",
          );
          return;
        }
        attempts += 1;
        retryTimer = setTimeout(renderWidget, 150);
        return;
      }

      try {
        containerRef.current.innerHTML = "";
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          callback: (token: string) => {
            onTokenChange(token);
            updateStatus("solved", "Verification complete.");
          },
          "expired-callback": () => {
            onTokenChange("");
            updateStatus("ready", "Complete the verification challenge to enable submit.");
          },
          "error-callback": () => {
            onTokenChange("");
            updateStatus(
              "error",
              "Verification couldn't load correctly. Retry and try again.",
            );
          },
          theme: "light",
        });
        updateStatus("ready", "Complete the verification challenge to enable submit.");
      } catch {
        updateStatus(
          "error",
          "Verification couldn't load correctly. Retry and try again.",
        );
      }
    };

    renderWidget();

    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Best-effort cleanup for remounts and reset cycles.
        }
      }
      widgetIdRef.current = null;
    };
  }, [
    action,
    onTokenChange,
    resetKey,
    retryKey,
    scriptFailed,
    scriptReady,
    siteKey,
    onStatusChange,
    updateStatus,
  ]);

  if (process.env.NEXT_PUBLIC_COSTDOC_E2E === "1" || siteKey === "e2e-bypass") {
    return (
      <div className={className}>
        <p className="text-xs font-medium text-slate-500">
          Verification challenge bypassed for E2E.
        </p>
      </div>
    );
  }

  if (!siteKey && process.env.NODE_ENV !== "production") {
    return (
      <div className={className}>
        <p className="text-xs font-medium text-slate-500">
          Verification challenge bypassed in local development.
        </p>
      </div>
    );
  }

  if (!siteKey) {
    return (
      <div className={className}>
        <p className="text-sm font-medium text-rose-700">
          Verification is temporarily unavailable. Please try again later.
        </p>
      </div>
    );
  }

  const displayStatus = scriptFailed
    ? "error"
    : !scriptReady
      ? "loading"
      : widgetStatus;
  const displayMessage = scriptFailed
    ? "We couldn't load the verification challenge. Retry and try again."
    : !scriptReady
      ? "Security check loading..."
      : statusMessage;

  return (
    <div ref={wrapperRef} className={className}>
      {/* Script is only injected once the widget is visible in the viewport.
          This prevents Cloudflare's iframe from running (and emitting Trusted
          Types console errors) on pages where the user hasn't scrolled here. */}
      {inViewport && (
        <Script
          key={`turnstile-script-${retryKey}`}
          src={TURNSTILE_SCRIPT_SRC}
          strategy="afterInteractive"
          onLoad={() => {
            setScriptFailed(false);
            setScriptReady(true);
            updateStatus("loading", "Security check loading...");
          }}
          onError={() => {
            setScriptFailed(true);
            updateStatus(
              "error",
              "We couldn't load the verification challenge. Retry and try again.",
            );
          }}
        />
      )}
      <div ref={containerRef} className="min-h-[65px]" />
      <div className="mt-3 space-y-3" data-nosnippet>
        {displayStatus === "loading" && (
          <div className="flex items-center gap-2" aria-live="polite">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
            <p className="text-sm font-medium text-slate-500">
              Verifying your browser...
            </p>
          </div>
        )}
        {displayStatus === "ready" && (
          <p className="text-sm font-medium text-slate-500" aria-live="polite">
            {displayMessage}
          </p>
        )}
        {displayStatus === "solved" && (
          <p className="text-sm font-medium text-emerald-700" aria-live="polite">
            {displayMessage}
          </p>
        )}
        {displayStatus === "error" && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="text-sm font-medium text-rose-700" aria-live="polite">
              {displayMessage}
            </p>
            <Button
              type="button"
              variant="tertiary"
              className="mt-3 h-10 rounded-xl border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
              onClick={() => {
                setScriptReady(Boolean(window.turnstile));
                setScriptFailed(false);
                setRetryKey((current) => current + 1);
              }}
            >
              Retry verification
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
